/**
 * Video format validation utilities
 * Handles resolution validation based on organization plans
 */

import type { ResizeValue, VideoFormatConfig } from "@repo/types";
import { VideoFormat } from "@repo/types";

export interface ResolutionLimits {
  maxWidth: number;
  maxHeight: number;
  maxResolution: number; // The larger of width or height
}

export interface ValidationResult {
  isValid: boolean;
  error?: string;
  suggestedResolution?: ResizeValue;
}

/**
 * Calculate the maximum possible resolution from organization plan
 * The max resolution is height x height (square format using plan height)
 */
export function calculateMaxResolution(planResolution: { width: number; height: number }): ResolutionLimits {
  const maxResolution = planResolution.height; // Use height as the maximum dimension
  
  return {
    maxWidth: planResolution.width,
    maxHeight: planResolution.height,
    maxResolution, // This is now the height value
  };
}

/**
 * Validate if a given resolution respects the organization's plan limits
 * Max resolution is height x height from the plan
 */
export function validateResolution(
  requestedResolution: { width: number; height: number },
  planResolution: { width: number; height: number }
): ValidationResult {
  const limits = calculateMaxResolution(planResolution);
  
  // Check if both dimensions are within the max resolution (height x height)
  if (requestedResolution.width > limits.maxResolution || requestedResolution.height > limits.maxResolution) {
    return {
      isValid: false,
      error: `Resolution ${requestedResolution.width}x${requestedResolution.height} exceeds plan maximum resolution of ${limits.maxResolution}x${limits.maxResolution}`,
      suggestedResolution: {
        width: limits.maxResolution,
        height: limits.maxResolution,
        name: VideoFormat.SQUARE_1_1, // Default to square (height x height)
      },
    };
  }
  
  return {
    isValid: true,
  };
}

/**
 * Get the optimal resolution for a given format within plan limits
 * Max resolution is height x height from the plan
 */
export function getOptimalResolutionForFormat(
  format: VideoFormatConfig,
  planResolution: { width: number; height: number }
): ResizeValue {
  const limits = calculateMaxResolution(planResolution);
  const { name } = format.value;
  const maxDimension = limits.maxResolution; // This is the plan height
  
  switch (name) {
    case VideoFormat.LANDSCAPE_16_9: {
      // Landscape: use max dimension as width, calculate height maintaining 16:9 ratio
      let width = maxDimension;
      let height = Math.floor((maxDimension * 9) / 16);
      
      // If calculated height exceeds max dimension, use max dimension and calculate width
      if (height > maxDimension) {
        height = maxDimension;
        width = Math.floor((maxDimension * 16) / 9);
      }
      return { width, height, name };
    }
    case VideoFormat.PORTRAIT_9_16: {
      // Portrait: use max dimension as height, calculate width maintaining 9:16 ratio
      let height = maxDimension;
      let width = Math.floor((maxDimension * 9) / 16);
      
      // If calculated width exceeds max dimension, use max dimension and calculate height
      if (width > maxDimension) {
        width = maxDimension;
        height = Math.floor((maxDimension * 16) / 9);
      }
      return { width, height, name };
    }
    case VideoFormat.SQUARE_1_1: {
      // Square: use max dimension for both width and height
      return { width: maxDimension, height: maxDimension, name };
    }
    case VideoFormat.LANDSCAPE_4_3: {
      let width = maxDimension;
      let height = Math.floor((maxDimension * 3) / 4);
      
      if (height > maxDimension) {
        height = maxDimension;
        width = Math.floor((maxDimension * 4) / 3);
      }
      return { width, height, name };
    }
    case VideoFormat.PORTRAIT_3_4: {
      let height = maxDimension;
      let width = Math.floor((maxDimension * 3) / 4);
      
      if (width > maxDimension) {
        width = maxDimension;
        height = Math.floor((maxDimension * 4) / 3);
      }
      return { width, height, name };
    }
    case VideoFormat.LANDSCAPE_21_9: {
      let width = maxDimension;
      let height = Math.floor((maxDimension * 9) / 21);
      
      if (height > maxDimension) {
        height = maxDimension;
        width = Math.floor((maxDimension * 21) / 9);
      }
      return { width, height, name };
    }
    case VideoFormat.PORTRAIT_9_21: {
      let height = maxDimension;
      let width = Math.floor((maxDimension * 9) / 21);
      
      if (width > maxDimension) {
        width = maxDimension;
        height = Math.floor((maxDimension * 21) / 9);
      }
      return { width, height, name };
    }
    case VideoFormat.LANDSCAPE_5_4: {
      let width = maxDimension;
      let height = Math.floor((maxDimension * 4) / 5);
      
      if (height > maxDimension) {
        height = maxDimension;
        width = Math.floor((maxDimension * 5) / 4);
      }
      return { width, height, name };
    }
    case VideoFormat.PORTRAIT_4_5: {
      let height = maxDimension;
      let width = Math.floor((maxDimension * 4) / 5);
      
      if (width > maxDimension) {
        width = maxDimension;
        height = Math.floor((maxDimension * 5) / 4);
      }
      return { width, height, name };
    }
    default:
      return format.value;
  }
}

/**
 * Validate and potentially adjust resolution for short video renders
 * Max resolution is height x height from the plan
 */
export function validateShortVideoResolution(
  requestedResolution: { width: number; height: number } | undefined,
  planResolution: { width: number; height: number }
): { resolution: { width: number; height: number }; isValid: boolean; error?: string } {
  // If no resolution provided, use plan default
  if (!requestedResolution) {
    return {
      resolution: planResolution,
      isValid: true,
    };
  }
  
  const validation = validateResolution(requestedResolution, planResolution);
  
  if (!validation.isValid) {
    // Fallback to max resolution (height x height)
    const maxDimension = planResolution.height;
    return {
      resolution: { width: maxDimension, height: maxDimension },
      isValid: false,
      error: validation.error,
    };
  }
  
  return {
    resolution: requestedResolution,
    isValid: true,
  };
}

/**
 * Validate and potentially adjust resolution for design renders
 * Max resolution is height x height from the plan
 */
export function validateDesignResolution(
  requestedResolution: { width: number; height: number } | undefined,
  planResolution: { width: number; height: number }
): { resolution: { width: number; height: number }; isValid: boolean; error?: string } {
  // If no resolution provided, use plan default
  if (!requestedResolution) {
    return {
      resolution: planResolution,
      isValid: true,
    };
  }
  
  const validation = validateResolution(requestedResolution, planResolution);
  
  if (!validation.isValid) {
    // Fallback to max resolution (height x height)
    const maxDimension = planResolution.height;
    return {
      resolution: { width: maxDimension, height: maxDimension },
      isValid: false,
      error: validation.error,
    };
  }
  
  return {
    resolution: requestedResolution,
    isValid: true,
  };
}

/**
 * Get all available formats for a given plan resolution
 * Max resolution is height x height from the plan
 */
export function getAvailableFormatsForPlan(
  formats: VideoFormatConfig[],
  planResolution: { width: number; height: number }
): VideoFormatConfig[] {
  return formats.map(format => ({
    ...format,
    value: getOptimalResolutionForFormat(format, planResolution)
  }));
}
